class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      cartItems.updateQuantity(this.dataset.index, 0, event);
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.onStandardEventsReady = () => {
      if (this.getAttribute('view-event-trigger') !== 'manual' || this.closest('cart-drawer')?.classList.contains('active')) {
        this.dispatchViewEvent();
      }
    };
    if (window.StandardEvents) {
      queueMicrotask(this.onStandardEventsReady);
    } else {
      document.addEventListener('lc:standard-events-ready', this.onStandardEventsReady, { once: true });
    }
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }
      return this.onCartUpdate(event.cartData, event.source === 'external-refresh');
    });
  }

  disconnectedCallback() {
    document.removeEventListener('lc:standard-events-ready', this.onStandardEventsReady);
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  dispatchViewEvent() {
    const { CartViewEvent } = window.StandardEvents || {};
    if (!CartViewEvent || !this.isConnected) return;
    const payload = this.getAttribute('view-event-payload');
    if (!payload) return;
    try {
      this.dispatchEvent(new CartViewEvent(JSON.parse(payload)));
    } catch (error) {
      console.warn('[Dawn] Cart view unavailable.', error);
    }
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;
    let message = '';

    if (inputValue < event.target.dataset.min) {
      message = window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min);
    } else if (inputValue > parseInt(event.target.max)) {
      message = window.quickOrderListStrings.max_error.replace('[max]', event.target.max);
    } else if (inputValue % parseInt(event.target.step) !== 0) {
      message = window.quickOrderListStrings.step_error.replace('[step]', event.target.step);
    }

    if (message) {
      this.setValidity(event, index, message);
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.updateQuantity(
        index,
        inputValue,
        event,
        document.activeElement.getAttribute('name'),
        event.target.dataset.quantityVariantId
      );
    }
  }

  onChange(event) {
    this.validateQuantity(event);
  }

  async onCartUpdate(cartData, strict = false) {
    try {
      const isDrawer = this.tagName === 'CART-DRAWER-ITEMS';
      const sectionId = isDrawer ? 'cart-drawer' : document.getElementById('main-cart-items')?.dataset.id || 'main-cart-items';
      let responseText = cartData?.sections?.[sectionId];
      if (typeof responseText !== 'string') {
        const response = await fetch(`${routes.cart_url}?section_id=${encodeURIComponent(sectionId)}`);
        if (!response.ok) throw new Error('Cart section request failed');
        responseText = await response.text();
      }
      if (isDrawer) {
        this.closest('cart-drawer').refreshContents(responseText);
      } else {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const source = html.querySelector('cart-items');
        if (!source) throw new Error('Cart items section missing');
        this.classList.toggle('is-empty', source.classList.contains('is-empty'));
        if (source.hasAttribute('view-event-payload')) this.setAttribute('view-event-payload', source.getAttribute('view-event-payload'));
        this.innerHTML = source.innerHTML;
      }
    } catch (error) {
      if (strict) throw error;
      console.error(error);
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents',
      },
      ...(document.querySelector('cart-drawer-items')
        ? [{ id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' }]
        : []),
    ];
  }

  updateQuantity(line, quantity, event, name, variantId) {
    const eventTarget = event.currentTarget instanceof CartRemoveButton ? 'clear' : 'change';
    const cartPerformanceUpdateMarker = CartPerformance.createStartingMarker(`${eventTarget}:user-action`);

    this.enableLoading(line);

    const sectionsToRender = this.getSectionsToRender();
    const quantityInput = this.querySelector(`#Quantity-${line}, #Drawer-quantity-${line}`);
    const lineKey = quantityInput?.dataset.quantityLineKey;
    const operation = lineKey ? window.DawnCartEvents?.start(this, 'CartLinesUpdateEvent', {
      action: quantity === 0 ? 'remove' : 'update',
      context: this.closest('cart-drawer') ? 'dialog' : 'cart',
      lines: [{ id: lineKey, quantity }],
    }) : null;
    let mutationAccepted = false;

    const body = JSON.stringify({
      line,
      quantity,
      sections: sectionsToRender.map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then(async (response) => {
        const state = await response.json();
        if (!response.ok) {
          if (response.status === 400 || response.status === 422) {
            return { ...state, errors: state.errors || state.description || state.message || window.cartStrings.error };
          }
          throw new Error(`Cart change failed: ${response.status}`);
        }
        return state;
      })
      .then((parsedState) => {
        if (parsedState.errors) {
          operation?.decline(parsedState.errors);
        } else {
          mutationAccepted = true;
          operation?.resolve(parsedState);
        }

        CartPerformance.measure(`${eventTarget}:paint-updated-sections`, () => {
          const quantityElement =
            document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
          const items = document.querySelectorAll('.cart-item');

          if (parsedState.errors) {
            quantityElement.value = quantityElement.getAttribute('value');
            this.updateLiveRegions(line, parsedState.errors);
            return;
          }

          this.classList.toggle('is-empty', parsedState.item_count === 0);
          const cartDrawerWrapper = document.querySelector('cart-drawer');
          const cartFooter = document.getElementById('main-cart-footer');

          if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
          if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

          sectionsToRender.forEach((section) => {
            const elementToReplace =
              document.getElementById(section.id).querySelector(section.selector) ||
              document.getElementById(section.id);
            const sectionDocument = new DOMParser().parseFromString(parsedState.sections[section.section], 'text/html');
            elementToReplace.innerHTML = sectionDocument.querySelector(section.selector).innerHTML;
            sectionDocument.querySelectorAll('cart-items, cart-drawer-items').forEach((source) => {
              const target = document.querySelector(source.localName);
              if (!target) return;
              target.classList.toggle('is-empty', source.classList.contains('is-empty'));
              if (source.hasAttribute('view-event-payload')) {
                target.setAttribute('view-event-payload', source.getAttribute('view-event-payload'));
              }
            });
          });
          cartDrawerWrapper?.prepareCartNote?.();
          const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
          let message = '';
          if (items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
            if (typeof updatedValue === 'undefined') {
              message = window.cartStrings.error;
            } else {
              message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
            }
          }
          this.updateLiveRegions(line, message);

          const lineItem = document.getElementById(
            this.tagName === 'CART-DRAWER-ITEMS' ? `CartDrawer-Item-${line}` : `CartItem-${line}`
          );
          const disclosure = document.querySelector('cart-disclosure-modal[open]');
          if (disclosure) {
            disclosure.trapFocusInModal();
          } else if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
            cartDrawerWrapper?.classList.contains('active') && cartDrawerWrapper.contains(lineItem)
              ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
              : lineItem.querySelector(`[name="${name}"]`).focus();
          } else if (
            parsedState.item_count === 0 &&
            cartDrawerWrapper?.classList.contains('active') &&
            cartDrawerWrapper.querySelector('.drawer__inner-empty')
          ) {
            cartDrawerWrapper.trapFocusInDrawer();
          } else if (cartDrawerWrapper?.classList.contains('active') && cartDrawerWrapper.querySelector('.cart-item')) {
            trapFocus(cartDrawerWrapper, cartDrawerWrapper.querySelector('.cart-item__name'));
          }
        });

        if (!parsedState.errors) publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId });
      })
      .catch((error) => {
        if (!mutationAccepted) operation?.reject(error);
        this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        if (errors) errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
        CartPerformance.measureFromMarker(`${eventTarget}:user-action`, cartPerformanceUpdateMarker);
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').textContent = message;

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }
}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'input',
          debounce((event) => {
            const note = event.target.value;
            const operation = window.DawnCartEvents?.start(this, 'CartNoteUpdateEvent', {
              context: this.closest('cart-drawer') ? 'dialog' : 'cart',
              note,
            });
            const body = JSON.stringify({ note });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } })
              .then(async (response) => {
                const cart = await response.json();
                if (!response.ok) {
                  if (response.status === 400 || response.status === 422) {
                    return operation?.decline(cart.errors || cart.description || cart.message || window.cartStrings.error);
                  }
                  throw new Error(`Cart note failed: ${response.status}`);
                }
                if (cart.errors) return operation?.decline(cart.errors);
                return operation?.resolve(cart);
              })
              .catch((error) => {
                operation?.reject(error);
                console.error(error);
              })
              .finally(() => CartPerformance.measureFromEvent('note-update:user-action', event));
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}
